import {
  QualityStatus,
  RegistrationDetails,
} from "@/registration-tools/utils/metrics";
import {
  FaroButton,
  FaroButtonSizes,
  FaroPopover,
  FaroSvgIcon,
  FaroText,
  useIsDarkScheme,
} from "@faro-lotv/flat-ui";
import {
  RegistrationEdgeType,
  RegistrationState,
} from "@faro-lotv/service-wires";
import { PopperPlacementType, Stack } from "@mui/material";
import { useCallback, useMemo, type JSX } from "react";
import { ReactComponent as RegistrationQualityIndicatorGoodDark } from "../icons/registration-quality-indicator-good-dark.svg";
import { ReactComponent as RegistrationQualityIndicatorGoodLight } from "../icons/registration-quality-indicator-good-light.svg";
import { ReactComponent as RegistrationQualityIndicatorMediumDark } from "../icons/registration-quality-indicator-medium-dark.svg";
import { ReactComponent as RegistrationQualityIndicatorMediumLight } from "../icons/registration-quality-indicator-medium-light.svg";
import { ReactComponent as RegistrationQualityIndicatorPoorDark } from "../icons/registration-quality-indicator-poor-dark.svg";
import { ReactComponent as RegistrationQualityIndicatorPoorLight } from "../icons/registration-quality-indicator-poor-light.svg";
import { ReactComponent as VectorSvg } from "../icons/vector.svg";
import {
  ConnectionScanNames,
  ConnectionScans,
} from "../rendering/registration-connections";

// Descriptions for overall project registration quality
const OVERALL_QUALITY_STATUS_GOOD =
  "The overall scan alignment is precise which ensures accurate measurements in the published data.";
const OVERALL_QUALITY_STATUS_MEDIUM =
  "The scan alignment is okay, but there are issues which might affect measuring accuracy. Review and consider editing if necessary.";
const OVERALL_QUALITY_STATUS_POOR =
  "The scan alignment has significant issues. Please review, edit and fix.";
const OVERALL_QUALITY_STATUS_UNKNOWN =
  "The registration is not yet completed. Edit and refine to calculate quality information.";
const OVERALL_QUALITY_STATUS_UNAVAILABLE =
  "The registration was completed but the quality information is not available. Please reach out to support.";

const OVERALL_QUALITY_TITLE = "Total registration quality";
const OVERALL_QUALITY_UNKNOWN_TITLE = "Registration quality unavailable";
const CLUSTER_BUTTON_TEXT = "Show detailed report";

// Descriptions for connection quality
const CONNECTION_QUALITY_GOOD_OVERLAP_AND_POINT_DISTANCE =
  "The scans are well-aligned with a small point distance and high overlap. The connection is strong and reliable.";
const CONNECTION_QUALITY_MEDIUM_OVERLAP_GOOD_POINT_DISTANCE =
  "The scans are well aligned but the overlap is moderate, which could impact reliability. The connection may need a review.";
const CONNECTION_QUALITY_WITH_MEDIUM_OVERLAP_AND_POINT_DISTANCE =
  "The scans are moderately aligned, but some deviations exist. The connection is usable but may need a review.";
const CONNECTION_QUALITY_WITH_POOR_OVERLAP_GOOD_OR_MEDIUM_POINTS_DISTANCE =
  "The scans are well aligned but the overlap is low, which could impact reliability. Please review the alignment visually.";
const CONNECTION_QUALITY_WITH_POOR_OVERLAP_AND_POINTS_DISTANCE =
  "The scans are poorly aligned. The connection is weak and may affect accuracy.";
const CONNECTION_QUALITY_MANUALLY_ADDED_EDGE =
  "This manually added connection failed to meet quality thresholds and could not be calculated.";

const CONNECTION_QUALITY_TITLE = "Pairwise registration quality";
const CONNECTIONS_MISSING =
  "The scan alignment has missing connections. Please review, edit and fix.";
const CONNECTION_BUTTON_TEXT = "Show details";

/** Scope to publish the quality, either for the root cluster or for a single connection  */
export enum QualityScope {
  CLUSTER = "CLUSTER",
  CONNECTION = "CONNECTION",
}

/**
 * Virtual element type for popover anchor
 * This is used to support popover anchor elements that are not part of the DOM tree.
 */
export type VirtualElement = {
  getBoundingClientRect(): ClientRect | DOMRect;
  contextElement?: Element;
};

export type QualityPopoverProps = {
  /** Scope to publish the quality for */
  scope: QualityScope;
  /** The quality of the connection or overall registration quality of cluster */
  quality: QualityStatus;
  /** Registration details */
  registrationDetails?: RegistrationDetails;
  /** The anchor element to tag the pop over */
  anchorEl: VirtualElement | HTMLElement | null;
  /** The placement orientation of the popover */
  placement?: PopperPlacementType;
  /** Is the brief registration quality popover open */
  isOpen: boolean;
  /** Call back when the pop over is closed */
  closePopover(): void;
  /** Call back when detailed registration quality information has to be shown */
  onDetailsButtonClick(): void;
  /** Is the report available in the capture tree root */
  isReportInCaptureTree?: boolean;
  /** Are global edges there in registration revision */
  areGlobalEdgesInRegistration?: boolean;
  /** Scan names of the connection */
  connectionScans?: ConnectionScans;
  /** The current revision registration status */
  revisionStatus?: RegistrationState;
  /** The current edge's registrationEdgeType */
  registrationEdgeType?: RegistrationEdgeType;
};

/**
 * @returns A popover the displays brief registration report
 */
export function QualityPopover({
  quality,
  registrationDetails,
  scope,
  anchorEl,
  placement,
  isOpen,
  onDetailsButtonClick,
  closePopover,
  isReportInCaptureTree,
  areGlobalEdgesInRegistration,
  connectionScans,
  revisionStatus,
  registrationEdgeType,
  ...rest
}: QualityPopoverProps): JSX.Element {
  const pointDistanceQuality =
    registrationDetails?.pointsDistanceDetails.quality ?? QualityStatus.UNKNOWN;
  const isDarkMode = useIsDarkScheme();

  const clusterDescription = useMemo(() => {
    switch (quality) {
      case QualityStatus.GOOD:
        return OVERALL_QUALITY_STATUS_GOOD;
      case QualityStatus.MEDIUM:
        return OVERALL_QUALITY_STATUS_MEDIUM;
      case QualityStatus.POOR:
        return OVERALL_QUALITY_STATUS_POOR;
      case QualityStatus.WARNING:
        return CONNECTIONS_MISSING;
      default:
        if (!isReportInCaptureTree && !areGlobalEdgesInRegistration) {
          return "";
        } else if (areGlobalEdgesInRegistration && !isReportInCaptureTree) {
          return OVERALL_QUALITY_STATUS_UNAVAILABLE;
        }
        return OVERALL_QUALITY_STATUS_UNKNOWN;
    }
  }, [quality, isReportInCaptureTree, areGlobalEdgesInRegistration]);

  const connectionDescription = useMemo(() => {
    switch (quality) {
      case QualityStatus.GOOD:
        return CONNECTION_QUALITY_GOOD_OVERLAP_AND_POINT_DISTANCE;
      case QualityStatus.MEDIUM:
        return pointDistanceQuality === QualityStatus.GOOD
          ? CONNECTION_QUALITY_MEDIUM_OVERLAP_GOOD_POINT_DISTANCE
          : CONNECTION_QUALITY_WITH_MEDIUM_OVERLAP_AND_POINT_DISTANCE;
      case QualityStatus.POOR:
        return pointDistanceQuality === QualityStatus.POOR
          ? CONNECTION_QUALITY_WITH_POOR_OVERLAP_AND_POINTS_DISTANCE
          : CONNECTION_QUALITY_WITH_POOR_OVERLAP_GOOD_OR_MEDIUM_POINTS_DISTANCE;
      case QualityStatus.UNKNOWN:
        if (
          revisionStatus === RegistrationState.registered &&
          registrationEdgeType === RegistrationEdgeType.candidate
        ) {
          return CONNECTION_QUALITY_MANUALLY_ADDED_EDGE;
        }
        return "";
      default:
        return "";
    }
  }, [quality, pointDistanceQuality, revisionStatus, registrationEdgeType]);

  const { title, description, buttonText } = useMemo(() => {
    if (scope === QualityScope.CLUSTER) {
      const title =
        quality === QualityStatus.WARNING
          ? OVERALL_QUALITY_UNKNOWN_TITLE
          : OVERALL_QUALITY_TITLE;

      const description = clusterDescription;
      return {
        title,
        description,
        buttonText: CLUSTER_BUTTON_TEXT,
      };
    }
    // if the scope is connection
    return {
      title: CONNECTION_QUALITY_TITLE,
      description: connectionDescription,
      buttonText: CONNECTION_BUTTON_TEXT,
    };
  }, [scope, connectionDescription, quality, clusterDescription]);

  // Computing the opacity of the quality indicators
  // based on the qualityStatus and registration error
  const computeOpacity = useCallback(
    (desiredQuality: QualityStatus): string => {
      if (quality === desiredQuality) {
        return "100%";
      }
      return "40%";
    },
    [quality],
  );

  return (
    <FaroPopover
      title={title}
      anchorEl={anchorEl}
      placement={placement}
      open={isOpen}
      onClose={closePopover}
      dark
      sx={{ minWidth: "320px" }}
      {...rest}
    >
      <Stack direction="column" sx={{ gap: 1.5, alignItems: "center" }}>
        <Stack
          direction="row"
          sx={{ gap: 1, alignItems: "center", justifyContent: "space-around" }}
        >
          <Stack
            direction="column"
            sx={{
              gap: 0.5,
              alignItems: "center",
              opacity: computeOpacity(QualityStatus.POOR),
            }}
            aria-label="Quality Indicator Poor"
          >
            <FaroSvgIcon
              source={
                isDarkMode
                  ? RegistrationQualityIndicatorPoorDark
                  : RegistrationQualityIndicatorPoorLight
              }
              sx={{
                width: "40px",
                height: "40px",
              }}
            />
            <FaroText variant="labelM" dark>
              {" "}
              POOR
            </FaroText>
          </Stack>

          <FaroSvgIcon source={VectorSvg} />
          <Stack
            direction="column"
            sx={{
              gap: 0.5,
              alignItems: "center",
              opacity: computeOpacity(QualityStatus.MEDIUM),
            }}
          >
            <FaroSvgIcon
              source={
                isDarkMode
                  ? RegistrationQualityIndicatorMediumDark
                  : RegistrationQualityIndicatorMediumLight
              }
              sx={{
                width: "40px",
                height: "40px",
              }}
              aria-label="Quality Indicator Medium"
            />
            <FaroText variant="labelM" dark>
              {" "}
              MEDIUM
            </FaroText>
          </Stack>

          <FaroSvgIcon source={VectorSvg} />
          <Stack
            direction="column"
            sx={{
              gap: 0.5,
              alignItems: "center",
              opacity: computeOpacity(QualityStatus.GOOD),
            }}
          >
            <FaroSvgIcon
              source={
                isDarkMode
                  ? RegistrationQualityIndicatorGoodDark
                  : RegistrationQualityIndicatorGoodLight
              }
              sx={{
                width: "40px",
                height: "40px",
              }}
              aria-label="Quality Indicator Good"
            />
            <FaroText variant="labelM" dark>
              {" "}
              GOOD
            </FaroText>
          </Stack>
        </Stack>
        {scope === QualityScope.CONNECTION && connectionScans !== undefined && (
          <ConnectionScanNames
            sourceScanName={connectionScans.sourceScanName}
            targetScanName={connectionScans.targetScanName}
            sx={{ alignSelf: "start" }}
          />
        )}
        <FaroText
          variant="bodyS"
          sx={{
            textAlign: "justify",
          }}
          dark
        >
          {description}
        </FaroText>
        {quality !== QualityStatus.UNKNOWN &&
          quality !== QualityStatus.WARNING && (
            <FaroButton
              size={FaroButtonSizes.s}
              aria-label="detailed quality report"
              onClick={() => {
                onDetailsButtonClick();
              }}
            >
              {" "}
              {buttonText}{" "}
            </FaroButton>
          )}
      </Stack>
    </FaroPopover>
  );
}
